// Copyright © 2019-2026 Focal Systems. All rights reserved.

/**
 * Parser for `CHANGELOG.md` (repo root), the source of truth shown in the sidebar
 * Changelog dialog. It follows Keep a Changelog 2.0.0
 * (https://keepachangelog.com/en/2.0.0/). Entries stay English-only by convention
 * (release notes are not translated).
 *
 * Supported markdown shape:
 *   ## [1.2.0] - 2026-07-01 (Codename)   ← release; date and "(codename)" optional
 *   ### Added                            ← Keep a Changelog category
 *   - item text                          ← list item ("-" or "*")
 * Everything else (preamble, blank lines, link-reference definitions) is ignored.
 *
 * This module is kept free of the `import.meta.glob` raw import so it stays
 * importable from the (Vite-less) Bun test runtime — the dialog wires the actual
 * CHANGELOG.md content into `parseChangelog`.
 */

export type ChangelogCategory = "Added" | "Changed" | "Deprecated" | "Removed" | "Fixed" | "Security";

export interface ChangelogChangeSet {
  category: ChangelogCategory;
  items: string[];
}

export interface ChangelogRelease {
  /** Semver, e.g. "1.0.0" — matches __APP_VERSION__. */
  version: string;
  /** Release codename (see the Release Codenames list in CLAUDE.md). */
  codename?: string;
  /** ISO date (YYYY-MM-DD). Absent for an unreleased entry. */
  date?: string;
  /** URL from the matching link-reference definition (`[version]: url`), if present. */
  url?: string;
  changes: ChangelogChangeSet[];
}

// `## [version] - date (codename)` — date and codename are optional.
const RELEASE_HEADING = /^##\s+\[([^\]]+)\](?:\s*-\s*(\d{4}-\d{2}-\d{2}))?(?:\s*\(([^)]+)\))?\s*$/;
const CATEGORY_HEADING = /^###\s+(.+?)\s*$/;
const LIST_ITEM = /^[-*]\s+(.+?)\s*$/;
// Link-reference definition at the bottom, e.g. `[1.0.0]: https://…/releases/tag/v1.0.0`.
const LINK_DEF = /^\[([^\]]+)\]:\s+(\S+)/;

export function parseChangelog(markdown: string): ChangelogRelease[] {
  const releases: ChangelogRelease[] = [];
  // version (lower-cased) → URL, from the link-reference definitions.
  const links: Record<string, string> = {};
  let release: ChangelogRelease | null = null;
  let changeSet: ChangelogChangeSet | null = null;

  for (const rawLine of markdown.split("\n")) {
    const line = rawLine.trimEnd();

    const linkMatch = LINK_DEF.exec(line);
    if (linkMatch?.[1] && linkMatch[2]) {
      links[linkMatch[1].toLowerCase()] = linkMatch[2];
      continue;
    }

    const releaseMatch = RELEASE_HEADING.exec(line);
    if (releaseMatch) {
      const version = releaseMatch[1];
      if (!version) continue;
      const date = releaseMatch[2];
      const codename = releaseMatch[3];
      release = { version, ...(date ? { date } : {}), ...(codename ? { codename } : {}), changes: [] };
      releases.push(release);
      changeSet = null;
      continue;
    }

    // Skip the preamble before the first release heading.
    if (!release) continue;

    const categoryMatch = CATEGORY_HEADING.exec(line);
    if (categoryMatch) {
      const category = categoryMatch[1];
      if (!category) continue;
      changeSet = { category: category as ChangelogCategory, items: [] };
      release.changes.push(changeSet);
      continue;
    }

    const item = LIST_ITEM.exec(line)?.[1];
    if (item && changeSet) {
      changeSet.items.push(item);
    }
  }

  // Attach link URLs and drop empty change sets / releases (e.g. an empty [Unreleased]).
  return releases
    .map((r) => {
      const url = links[r.version.toLowerCase()];
      return { ...r, ...(url ? { url } : {}), changes: r.changes.filter((c) => c.items.length > 0) };
    })
    .filter((r) => r.changes.length > 0);
}

// A leading `[TEAM-123]` token, e.g. `[CORE-3434]`. Only matched at the start.
const LEADING_TICKET = /^\s*\[([A-Z][A-Z0-9]*-\d+)\]\s*/;

/**
 * Split any leading Linear ticket reference(s) off a changelog item. By convention
 * tickets are prepended in brackets — `[CORE-3434] Did the thing` — and there may be
 * more than one. Returns the ticket IDs and the remaining message text.
 */
export function extractTickets(item: string): { tickets: string[]; text: string } {
  const tickets: string[] = [];
  let text = item;
  let match = LEADING_TICKET.exec(text);
  while (match?.[1]) {
    tickets.push(match[1]);
    text = text.slice(match[0].length);
    match = LEADING_TICKET.exec(text);
  }
  return { tickets, text };
}
